import { useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ragApi, RagApiError } from '../services/ApiService'
import type { ApiDirectory } from '../lib/types'
import { queryKeys } from '@/queries/keys'

export interface DirectoryNode {
  id: string
  name: string
  description?: string
  parent?: string | null
  position?: number
  level?: number
  children: DirectoryNode[]
}

interface UseDirectoriesResult {
  directories: DirectoryNode[]
  flatDirectories: { id: string; name: string; path: string }[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  createDirectory: (data: { name: string; description?: string; parent?: number | null }) => Promise<void>
  updateDirectory: (id: string, data: { name?: string; description?: string; parent?: number | null }) => Promise<void>
  deleteDirectory: (id: string) => Promise<void>
}

function mapApiDirToNode(api: ApiDirectory): DirectoryNode {
  return {
    id: String(api.id),
    name: api.name,
    description: api.description,
    parent: api.parent != null ? String(api.parent) : null,
    position: api.position,
    level: api.level,
    children: (api.children || []).map(mapApiDirToNode),
  }
}

function flattenWithPath(nodes: DirectoryNode[], prefix = ''): { id: string; name: string; path: string }[] {
  const result: { id: string; name: string; path: string }[] = []
  for (const node of nodes) {
    const path = prefix ? `${prefix} / ${node.name}` : node.name
    result.push({ id: node.id, name: node.name, path })
    if (node.children.length > 0) {
      result.push(...flattenWithPath(node.children, path))
    }
  }
  return result
}

export function useDirectories(): UseDirectoriesResult {
  const queryClient = useQueryClient()

  const directoriesQuery = useQuery({
    queryKey: queryKeys.knowledge.directoriesTree(),
    queryFn: async () => {
      const raw = (await ragApi.getDirectories({ tree: true })) as ApiDirectory[]
      return Array.isArray(raw) ? raw.map(mapApiDirToNode) : []
    },
    staleTime: 120_000,
    refetchOnMount: false,
    retry: 0,
  })

  const createDirectoryMutation = useMutation({
    mutationFn: async (data: { name: string; description?: string; parent?: number | null }) => {
      await ragApi.createDirectory(data)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.knowledge.directoriesTree() })
    },
  })

  const updateDirectoryMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: { name?: string; description?: string; parent?: number | null } }) => {
      await ragApi.updateDirectory(id, data)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.knowledge.directoriesTree() })
    },
  })

  const deleteDirectoryMutation = useMutation({
    mutationFn: async (id: string) => {
      await ragApi.deleteDirectory(id)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.knowledge.directoriesTree() })
    },
  })

  const createDirectory = useCallback(async (data: { name: string; description?: string; parent?: number | null }) => {
    try {
      await createDirectoryMutation.mutateAsync(data)
    } catch (err) {
      const msg = err instanceof RagApiError ? err.message : 'Failed to create directory'
      throw new Error(msg)
    }
  }, [createDirectoryMutation])

  const updateDirectory = useCallback(async (id: string, data: { name?: string; description?: string; parent?: number | null }) => {
    try {
      await updateDirectoryMutation.mutateAsync({ id, data })
    } catch (err) {
      const msg = err instanceof RagApiError ? err.message : 'Failed to update directory'
      throw new Error(msg)
    }
  }, [updateDirectoryMutation])

  const deleteDirectory = useCallback(async (id: string) => {
    try {
      await deleteDirectoryMutation.mutateAsync(id)
    } catch (err) {
      const msg = err instanceof RagApiError ? err.message : 'Failed to delete directory'
      throw new Error(msg)
    }
  }, [deleteDirectoryMutation])

  const directories = directoriesQuery.data || []

  return {
    directories,
    flatDirectories: flattenWithPath(directories),
    loading: directoriesQuery.isLoading,
    error: directoriesQuery.error instanceof Error ? directoriesQuery.error.message : null,
    refetch: async () => {
      await directoriesQuery.refetch()
    },
    createDirectory,
    updateDirectory,
    deleteDirectory,
  }
}
